/* -------------------------------------------------------
   ProfiTree AI Chat V2 — API Client
   Uses the named axios instance (apiClient) so
   credentials/401 handling is shared with all other calls.
   ------------------------------------------------------- */

import apiClient from './client';
import type {
  CreateJobRequest,
  CreateJobResponse,
  JobResponse,
  ConversationSummary,
  ConversationDetail,
} from '../types/chatV2';

/**
 * POST /v2/chat/jobs  →  202 { jobId }
 */
export async function createChatJob(
  request: CreateJobRequest,
): Promise<CreateJobResponse> {
  const res = await apiClient.post<CreateJobResponse>('/v2/chat/jobs', request);
  return res.data;
}

/**
 * GET /v2/chat/jobs/{jobId}  →  JobResponse
 * Normalizes backend response (e.g. actionable_items → actionableItems).
 */
export async function getChatJob(jobId: string): Promise<JobResponse> {
  const res = await apiClient.get<Record<string, unknown>>(
    `/v2/chat/jobs/${encodeURIComponent(jobId)}`,
  );
  const raw = res.data;
  // Backend may send actionable_items (snake_case); ensure we expose actionableItems
  const actionableItems = raw.actionableItems ?? raw.actionable_items;
  return { ...raw, actionableItems } as JobResponse;
}

/* -------------------------------------------------------
   Conversation history endpoints
   ------------------------------------------------------- */

/**
 * GET /v2/chat/conversations  →  ConversationSummary[]
 */
export async function listConversations(): Promise<ConversationSummary[]> {
  const res = await apiClient.get<any[]>('/v2/chat/conversations');
  return res.data.map((c) => ({
    id: c.id || c.conversationId,
    title: c.title ?? null,
    messageCount: c.messageCount ?? 0,
    lastMessageAt: c.lastMessageAt ?? c.updatedAt ?? c.createdAt,
    createdAt: c.createdAt,
  }));
}

/**
 * GET /v2/chat/conversations/{id}  →  ConversationDetail
 */
export async function getConversation(
  id: string,
): Promise<ConversationDetail> {
  const res = await apiClient.get<any>(
    `/v2/chat/conversations/${encodeURIComponent(id)}`,
  );
  const c = res.data;
  return {
    id: c.id || c.conversationId,
    title: c.title ?? null,
    messageCount: c.messageCount ?? 0,
    lastMessageAt: c.lastMessageAt ?? c.updatedAt ?? c.createdAt,
    createdAt: c.createdAt,
    messages: (c.messages ?? []).map((m: any) => ({
      id: m.id || m.messageId,
      role: m.role,
      content: m.content,
      createdAt: m.createdAt ?? m.timestamp,
    })),
  };
}

/**
 * PATCH /v2/chat/conversations/{id}  →  rename
 */
export async function renameConversation(
  id: string,
  title: string,
): Promise<void> {
  await apiClient.patch(
    `/v2/chat/conversations/${encodeURIComponent(id)}`,
    { title },
  );
}

/**
 * DELETE /v2/chat/conversations/{id}  →  soft-delete
 */
export async function deleteConversation(id: string): Promise<void> {
  await apiClient.delete(
    `/v2/chat/conversations/${encodeURIComponent(id)}`,
  );
}
